import { ErrorInfo, PropsWithChildren } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ReactErrorBoundary } from './react-error-boundary';
import { ServerErrorState } from './ServerErrorState';
import { datadogRum } from '@datadog/browser-rum';
import { AxiosError, isAxiosError } from 'axios';

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        DD_RUM?: typeof datadogRum;
    }
}

const trackError = (error: Error | AxiosError, errorInfo: ErrorInfo) => {
    const renderingError = new Error(error.message);
    renderingError.name = 'ReactRenderingError';
    renderingError.stack = errorInfo.componentStack ?? '';
    renderingError.cause = error;

    let errorContext: object = {
        userSawErrorBoundary: true,
    };

    if (isAxiosError(error)) {
        errorContext = {
            ...errorContext,
            requestHeaders: error.config?.headers,
            responseHeaders: error.response?.headers,
        };
    }

    if (window.DD_RUM) {
        window.DD_RUM.addError(renderingError, errorContext);
    }
};

/**
 * Component to catch and display errors in React components
 */
interface ErrorBoundaryProps {
    actionCausingError: string;
    onRetry?: () => void;
}

export const ErrorBoundary = ({
    children,
    actionCausingError,
    onRetry,
}: PropsWithChildren<ErrorBoundaryProps>) => {
    const queryClient = useQueryClient();

    return (
        <ReactErrorBoundary
            onError={trackError}
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ServerErrorState
                    errorDetails={{
                        error,
                        actionCausingError,
                    }}
                    onRetry={() => {
                        // Reset queries so they will be refetched when the user retries
                        queryClient.resetQueries();
                        onRetry?.();
                        resetErrorBoundary();
                    }}
                />
            )}
        >
            {children}
        </ReactErrorBoundary>
    );
};
